import { FunctionComponent, ReactNode, SVGProps, useState } from 'react';
import { Formik, Form, Field, FieldProps, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Select } from '@round/ui-kit';
import { microwave } from '@round/api';
import { showNotification } from 'utility/showNotification';
import { ReactComponent as TiktokIcon } from 'assets/TiktokIcon.svg';
import { ReactComponent as InstagramIcon } from 'assets/Instagram.svg';
import { ReactComponent as YoutubeIcon } from 'assets/YoutubeIcon.svg';
import { usePublicContentTags } from './usePublicContentTags';
import { LocationStep, LocationValue, emptyLocationValue, locationIdOf } from './components/LocationStep';
import { StepProgress } from './components/StepProgress';
import { waveSelectStyles } from './waveSelectStyles';

const SUPPORT_EMAIL = 'info@micro-wav.com';
const MIN_TAGS = 2;
const MAX_TAGS = 5;

type PlatformOption = { value: microwave.CreatorApplicationPlatform; label: string };

const PLATFORM_OPTIONS: PlatformOption[] = [
    { value: 'TIKTOK', label: 'TikTok' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'YOUTUBE', label: 'YouTube' },
];

const PLATFORM_ICONS: Record<microwave.CreatorApplicationPlatform, FunctionComponent<SVGProps<SVGSVGElement>>> = {
    TIKTOK: TiktokIcon,
    INSTAGRAM: InstagramIcon,
    YOUTUBE: YoutubeIcon,
};

const formatPlatformOption = (option: PlatformOption) => {
    const Icon = PLATFORM_ICONS[option.value];
    return (
        <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0" />
            {option.label}
        </span>
    );
};

const PLATFORM_DOMAINS: Record<microwave.CreatorApplicationPlatform, string[]> = {
    TIKTOK: ['tiktok.com'],
    INSTAGRAM: ['instagram.com', 'instagr.am'],
    YOUTUBE: ['youtube.com', 'youtu.be'],
};

type AccountValue = { platform: microwave.CreatorApplicationPlatform; handle: string };
type TagOption = { value: number; label: string };

type FormValues = {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    locationId: number | null;
    accounts: AccountValue[];
    otherSocials: string;
    contentTags: TagOption[];
};

const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    locationId: null,
    accounts: [{ platform: 'TIKTOK', handle: '' }],
    otherSocials: '',
    contentTags: [],
};

const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string().trim().email('Enter a valid email').required('Email is required'),
    confirmEmail: Yup.string()
        .trim()
        .oneOf([Yup.ref('email')], 'Emails must match')
        .required('Confirm your email'),
    locationId: Yup.number().nullable().required('Location is required'),
    accounts: Yup.array()
        .of(
            Yup.object().shape({
                platform: Yup.string().required(),
                handle: Yup.string()
                    .trim()
                    .required('Enter a handle or remove this row')
                    .max(200, 'This is too long for a single handle')
                    .test(
                        'single-handle',
                        'Enter a single handle, not multiple',
                        (value) => !value || !/\s/.test(value.trim())
                    )
                    .test('platform-url-match', "This link doesn't match the selected platform", function (value) {
                        if (!value) return true;
                        const text = value.toLowerCase();
                        const looksLikeUrl = /https?:\/\/|www\.|\.\w{2,}\//.test(text);
                        if (!looksLikeUrl) return true;
                        const platform = (this.parent as AccountValue).platform;
                        try {
                            const url = new URL(/^https?:\/\//.test(text) ? text : `https://${text}`);
                            const host = url.hostname.replace(/^www\./, '');
                            return PLATFORM_DOMAINS[platform].some(
                                (domain) => host === domain || host.endsWith(`.${domain}`)
                            );
                        } catch {
                            return false;
                        }
                    }),
            })
        )
        .min(1, 'Add at least one social account'),
    contentTags: Yup.array()
        .min(MIN_TAGS, `Select at least ${MIN_TAGS} tags`)
        .max(MAX_TAGS, `Select up to ${MAX_TAGS} tags`),
});

const STEP_FIELDS: (keyof FormValues)[][] = [
    ['firstName', 'lastName', 'email', 'confirmEmail'],
    ['accounts'],
    ['locationId'],
    ['contentTags'],
];

const STEP_TITLES = [
    'How should we contact you?',
    'Where can we find you online?',
    'Where are you based?',
    'What kind of content do you make?',
];

const FieldError = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="span" className="text-xs text-wm-danger" />
);

const RequiredLabel = ({ children }: { children: ReactNode }) => (
    <span className="wm-field__label">
        <span>
            {children} <span className="text-wm-danger">*</span>
        </span>
    </span>
);

const SignupPage = () => {
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [location, setLocation] = useState<LocationValue>(emptyLocationValue);
    // Captured once at mount: the hidden referral code from /signup?ref=<code>, never shown to the applicant.
    const [referralCode] = useState(() => new URLSearchParams(window.location.search).get('ref') ?? undefined);
    const { tags, isLoading: areTagsLoading } = usePublicContentTags();

    const tagOptions: TagOption[] = tags.map((tag) => ({ value: tag.id, label: tag.name }));

    const handleSubmit = async (values: FormValues) => {
        const body: microwave.PostCreatorApplicationBody = {
            first_name: values.firstName.trim(),
            last_name: values.lastName.trim(),
            email: values.email.trim(),
            location_id: values.locationId as number,
            other_socials_notes: values.otherSocials.trim(),
            content_tag_ids: values.contentTags.map((tag) => tag.value),
            accounts: values.accounts.map((account) => ({
                platform: account.platform,
                handle: account.handle.trim(),
            })),
            referral_code: referralCode,
        };

        try {
            const response = await microwave.postCreatorApplication(body);
            if (response.status === 201) {
                setSubmitted(true);
                return;
            }
            if (response.status === 429) {
                showNotification('Too many submissions. Please try again later.', 'error');
                return;
            }
            const detail = Object.values(response.data as Record<string, string[]>)
                .flat()
                .join(' ');
            showNotification(detail || `Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        }
    };

    if (submitted) {
        return (
            <div className="wm-root mx-auto max-w-[520px] p-6">
                <div className="wm-state">
                    <h2 className="wm-state__title">Thanks, we&apos;ll review your application</h2>
                    <p className="wm-state__body">We&apos;ll be in touch by email.</p>
                </div>
            </div>
        );
    }

    const isLastStep = step === STEP_FIELDS.length - 1;

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {(formik) => {
                const goNext = async () => {
                    const errors = await formik.validateForm();
                    const stepFields = STEP_FIELDS[step];
                    const hasStepError = stepFields.some((fieldName) => Boolean(errors[fieldName]));
                    if (hasStepError) {
                        if (step === 1) {
                            formik.values.accounts.forEach((_, index) =>
                                formik.setFieldTouched(`accounts.${index}.handle`, true, false)
                            );
                        } else {
                            stepFields.forEach((fieldName) => formik.setFieldTouched(fieldName, true, false));
                        }
                        return;
                    }
                    setStep((current) => current + 1);
                };

                return (
                    <Form className="wm-root mx-auto flex max-w-[520px] flex-col gap-4 p-6">
                        <div className="flex flex-col gap-3">
                            <StepProgress current={step} total={STEP_FIELDS.length} />
                            <p className="text-xs font-medium text-wm-ink-3">
                                Step {step + 1} of {STEP_FIELDS.length}
                            </p>
                            <h1 className="wm-h1 m-0!">{STEP_TITLES[step]}</h1>
                        </div>

                        {step === 0 && (
                            <>
                                <Field name="firstName">
                                    {({ field }: FieldProps) => (
                                        <label className="wm-field">
                                            <RequiredLabel>First name</RequiredLabel>
                                            <input className="wm-input" type="text" {...field} />
                                            <FieldError name="firstName" />
                                        </label>
                                    )}
                                </Field>
                                <Field name="lastName">
                                    {({ field }: FieldProps) => (
                                        <label className="wm-field">
                                            <RequiredLabel>Last name</RequiredLabel>
                                            <input className="wm-input" type="text" {...field} />
                                            <FieldError name="lastName" />
                                        </label>
                                    )}
                                </Field>
                                <Field name="email">
                                    {({ field }: FieldProps) => (
                                        <label className="wm-field">
                                            <RequiredLabel>Email</RequiredLabel>
                                            <input className="wm-input" type="email" {...field} />
                                            <FieldError name="email" />
                                        </label>
                                    )}
                                </Field>
                                <Field name="confirmEmail">
                                    {({ field }: FieldProps) => (
                                        <label className="wm-field">
                                            <RequiredLabel>Confirm email</RequiredLabel>
                                            <input className="wm-input" type="email" {...field} />
                                            <FieldError name="confirmEmail" />
                                        </label>
                                    )}
                                </Field>
                            </>
                        )}

                        {step === 1 && (
                            <FieldArray name="accounts">
                                {(arrayHelpers) => (
                                    <div className="flex flex-col gap-4">
                                        {formik.values.accounts.map((account, index) => {
                                            const accountErrors = formik.errors.accounts;
                                            const handleError =
                                                Array.isArray(accountErrors) && typeof accountErrors[index] === 'object'
                                                    ? (accountErrors[index] as { handle?: string }).handle
                                                    : undefined;
                                            const accountsTouched = formik.touched.accounts;
                                            const handleTouched =
                                                Array.isArray(accountsTouched) &&
                                                typeof accountsTouched[index] === 'object'
                                                    ? (accountsTouched[index] as { handle?: boolean }).handle
                                                    : false;
                                            return (
                                                <div key={index} className="flex flex-col gap-2">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="w-[10rem]">
                                                            <Select
                                                                aria-label="Platform"
                                                                styles={waveSelectStyles}
                                                                formatOptionLabel={formatPlatformOption}
                                                                options={PLATFORM_OPTIONS}
                                                                value={PLATFORM_OPTIONS.find(
                                                                    (o) => o.value === account.platform
                                                                )}
                                                                onChange={(option) =>
                                                                    formik.setFieldValue(
                                                                        `accounts.${index}.platform`,
                                                                        (
                                                                            option as {
                                                                                value: microwave.CreatorApplicationPlatform;
                                                                            }
                                                                        ).value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        {formik.values.accounts.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="wm-btn wm-btn--link"
                                                                onClick={() => arrayHelpers.remove(index)}
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                    <Field name={`accounts.${index}.handle`}>
                                                        {({ field }: FieldProps) => (
                                                            <input
                                                                className="wm-input"
                                                                type="text"
                                                                aria-label={`${
                                                                    PLATFORM_OPTIONS.find(
                                                                        (o) => o.value === account.platform
                                                                    )?.label ?? 'Social'
                                                                } handle or profile URL`}
                                                                placeholder="@handle or profile URL"
                                                                {...field}
                                                            />
                                                        )}
                                                    </Field>
                                                    {handleTouched && handleError && (
                                                        <span className="text-xs text-wm-danger">{handleError}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            className="wm-btn wm-btn--ghost"
                                            onClick={() => arrayHelpers.push({ platform: 'TIKTOK', handle: '' })}
                                        >
                                            Add another account
                                        </button>
                                        {typeof formik.errors.accounts === 'string' && (
                                            <span className="text-xs text-wm-danger">{formik.errors.accounts}</span>
                                        )}
                                        <label className="wm-field">
                                            <span className="wm-field__label">
                                                Any other socials? (Snapchat, Reddit, X…)
                                            </span>
                                            <Field name="otherSocials">
                                                {({ field }: FieldProps) => (
                                                    <textarea className="wm-input wm-textarea" {...field} />
                                                )}
                                            </Field>
                                        </label>
                                    </div>
                                )}
                            </FieldArray>
                        )}

                        {step === 2 && (
                            <>
                                <LocationStep
                                    value={location}
                                    onChange={(next) => {
                                        setLocation(next);
                                        formik.setFieldValue('locationId', locationIdOf(next));
                                    }}
                                />
                                <FieldError name="locationId" />
                            </>
                        )}

                        {step === 3 && (
                            <label className="wm-field">
                                <RequiredLabel>
                                    Pick {MIN_TAGS}&ndash;{MAX_TAGS} tags that describe your content
                                </RequiredLabel>
                                <Select
                                    styles={waveSelectStyles}
                                    inputId="contentTags"
                                    isMulti
                                    isDisabled={formik.isSubmitting}
                                    isLoading={areTagsLoading}
                                    options={tagOptions}
                                    value={formik.values.contentTags}
                                    onChange={(options) =>
                                        formik.setFieldValue('contentTags', (options as TagOption[]) ?? [])
                                    }
                                />
                                <span className="wm-field__hint">
                                    Tag yourself accurately. Inaccurate tags may get your application rejected.
                                </span>
                                {formik.touched.contentTags && typeof formik.errors.contentTags === 'string' && (
                                    <span className="text-xs text-wm-danger">{formik.errors.contentTags}</span>
                                )}
                            </label>
                        )}

                        <div className="mt-2 flex flex-col gap-3">
                            {isLastStep ? (
                                <button type="submit" className="wm-btn wm-btn--accent" disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? 'Submitting…' : 'Submit application'}
                                </button>
                            ) : (
                                <button type="button" className="wm-btn wm-btn--accent" onClick={goNext}>
                                    Next
                                </button>
                            )}
                            {step > 0 && (
                                <button
                                    type="button"
                                    className="wm-btn wm-btn--ghost w-full"
                                    disabled={formik.isSubmitting}
                                    onClick={() => setStep((current) => Math.max(current - 1, 0))}
                                >
                                    Back
                                </button>
                            )}
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default SignupPage;
